import React, { KeyboardEvent, memo, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch'
import { getValidationSchema } from '../../utils/getValidationSchema'
import { useFormValidation } from '../../../../../hooks/useFormValidation'
import { inputCall } from '../../../control'
import { buildControlsExt } from '../../../../inputs/controls'
import * as yup from 'yup'
import { showModal } from '../../../../../store/actions/modalActions'
import { ModalTypes } from '../../../../../types/modals'
import AppButton from '../../../../buttons/AppButton/AppButton'
import { DuplicateBtn } from '../../../../buttons/DuplicateBtn/DuplicateBtn'
import { MoveToPosition } from '../../../../overlays/MoveToPosition/MoveToPosition'
import { COUNTRIES, isCountries } from '../../../../../constants/countries'

interface TableRowAdvancedProps {
    data: any
    schema: any
    name: string
    index: number
    modifyTableRow: (name: string, value: any, index: number) => void
    deleteTableRow: (index: number) => void
    duplicateTableRow: (position: number) => void
    moveTableRow: (toPosition: number, index: number) => void
    onValidityCheck: (elementId: string, isValid: boolean) => void
    onItemFocus: (index: number) => void
    focusIndex: number
    itemsCount: number
    isEditMode: boolean
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>
    isOrderUserDefined: boolean
    onRowEdit: (elementId: string, isEditing: boolean) => void
    isRowEditing: boolean
    scalarsName?: string
}

export const TableRowAdvanced = memo(function TableRowAdvanced({
    data,
    schema,
    name,
    index,
    modifyTableRow,
    deleteTableRow,
    duplicateTableRow,
    moveTableRow,
    onValidityCheck,
    onItemFocus,
    focusIndex,
    itemsCount,
    isEditMode,
    setEditMode,
    isOrderUserDefined,
    onRowEdit,
    isRowEditing,
    scalarsName,
}: TableRowAdvancedProps) {
    const dispatch = useAppDispatch()

    const [stateDerivedFromData] = useState(data ?? {})

    const elementId = stateDerivedFromData.element_id
    const validationSchema = getValidationSchema(schema)

    const [inputState, setInputState, touched, setTouched, validationResult] =
        useFormValidation(stateDerivedFromData, validationSchema)

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e?.key === 'Enter' && isRowValid) {
            e.preventDefault()
            onRowEdit(elementId, false)
            if (focusIndex === index) onItemFocus(-1)
        }
    }

    const rowDataArray = Object.keys(schema).map((key: string) =>
        inputCall({
            type: schema[key]['type'],
            name: key,
            isLabelHidden: true,
            placeholder: schema[key]?.default ?? '',
            data: { selected: stateDerivedFromData[key] },
            changeWarning: schema[key].change_warning,
            schema: schema[key],
            options: isCountries(scalarsName) ? COUNTRIES : null,
            state: inputState,
            onEnter: handleKeyDown,
            onChange: (value: any) => modifyTableRow(key, value, index),
        })
    )

    const rowData = buildControlsExt(
        rowDataArray,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    const urlSchema = yup.object().shape(validationSchema)
    const hasTouchedFields = Object.keys(touched).length !== 0
    const isRowValid = urlSchema.isValidSync(inputState) && hasTouchedFields
    const position = index + 1

    const handleDelete = () =>
        dispatch(
            showModal(ModalTypes.MAIN_MODAL, {
                body: `Delete ${name} ${position}?`,
                onSubmit: onDeleteRow,
            })
        )

    const handlePositionChange = (toPosition: number) => {
        if (!isEditMode) {
            setEditMode(true)
        }
        moveTableRow(toPosition, index)
    }

    const handleOnDuplicate = () => {
        if (!isEditMode) {
            setEditMode(true)
        }
        duplicateTableRow(position)
        onItemFocus(position)
    }

    const handleOnEdit = () => {
        if (!isEditMode) {
            setEditMode(true)
        }
        onRowEdit(elementId, true)
    }

    const onDeleteRow = () => {
        if (!isEditMode) {
            setEditMode(true)
        }
        onRowEdit(elementId, false)
        deleteTableRow(index)
        if (focusIndex === index) onItemFocus(-1)
    }

    useEffect(() => {
        if (hasTouchedFields) {
            onValidityCheck(elementId, isRowValid)
        }
    }, [isRowValid, hasTouchedFields])

    useEffect(() => {
        if (focusIndex === index && !isRowEditing) {
            onRowEdit(elementId, true)
        }
    }, []) // eslint-disable-line

    return (
        <>
            <td
                className={`${isRowEditing ? 'pt-4 bg-white-blue' : ''}`}
                onClick={handleOnEdit}
            >
                {position}
            </td>

            {Object.keys(schema)
                .filter((key: any) => key !== 'element_id')
                .map((keyName: any, i: number) =>
                    isRowEditing ? (
                        <td
                            key={keyName}
                            className="bg-white-blue"
                        >
                            {rowData[i]}
                        </td>
                    ) : schema[keyName]?.['type'] === 'secret' ? (
                        <td
                            key={keyName}
                            onClick={handleOnEdit}
                        >
                            ******
                        </td>
                    ) : (
                        <td
                            key={keyName}
                            onClick={handleOnEdit}
                        >
                            {String(stateDerivedFromData[keyName])}
                        </td>
                    )
                )}

            <td
                style={{ verticalAlign: 'top' }}
                className={isRowEditing ? 'bg-white-blue' : ''}
            >
                <div
                    className={`d-flex justify-content-center ${
                        isRowEditing && 'mt-2'
                    }`}
                >
                    {isOrderUserDefined && (
                        <MoveToPosition
                            position={position}
                            maxPosition={itemsCount}
                            onSubmit={handlePositionChange}
                            disabled={false}
                            className="btn light-blue"
                            popoverPlacement="left"
                        />
                    )}
                    <DuplicateBtn
                        onClick={handleOnDuplicate}
                        className="btn light-blue"
                    />
                    <AppButton
                        variant="delete"
                        onClick={handleDelete}
                        className="bleak-red"
                        tooltip="tooltips.deleteRow"
                    />
                </div>
            </td>
        </>
    )
})
